import { useContext, useState,useMemo } from "react";
import MultiComparisonComponent from "../../Dashboard/Component/MultiComparisonComponent";
import { DiscoveryContext, useIsSelectedSection } from "../discovery-context";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import classes from "./BrandSummary.module.css";
import { AuthContext } from "../../../context/auth-context";
import { filterKey } from "../BrandAudit";
import Checkbox from "@mui/material/Checkbox";
import { tab } from "../BrandScore/BrandScore";
import MUIIconButton from "./../../../Component/UI/MUIIconButton/MUIIconButton";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import Modal from "../../../Component/Modal/Modal";
import Share from "../Share/Share";
import { toast } from "react-toastify";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

export default function BrandSummary() {
  const discoveryCtx = useContext(DiscoveryContext);
  const [showShareModal, setShowShareModal] = useState(false);
  const { user } = useContext(AuthContext);
  const customerId = user?.customer_id || 0;
  let primary_llm = discoveryCtx.primary_llm;
  let llms = discoveryCtx.llm;
  const selectedMarket = discoveryCtx.state.selectedMarket;
  const selectedPersona = discoveryCtx.state.selectedPersona;
  const discoveryType = discoveryCtx.type || "brand";
  const id = `Brand_Summary`;
  let uniqueKey = `${tab.brandOverview}_${id}_${selectedMarket}_${selectedPersona}`;
  const { checked } = useIsSelectedSection({ id, uniqueKey, tab: tab.brandOverview });
  
const personaOptions = useMemo(() => {
  return (discoveryCtx?.persona || [])
    .map(p => {
      // New record format
      if (typeof p === "object" && p?.title) {
        return {
          name: p.title,
          value: p.title,
        };
      }

      // Old record format
      if (typeof p === "string") {
        return {
          name: p,
          value: p,
        };
      }

      return null;
    })
    .filter(Boolean);
}, [discoveryCtx?.persona]);


  const getBrandScores = (type) => {
    const brandScoresMap = {};

    llms?.forEach(llm => {
      const scores = discoveryCtx?.brand_scores?.[llm] || {};
      const categories = Object.keys(scores);

      categories.forEach(category => {
        const categoryNode = scores[category];

        // ---- CASE 1: No markets → category = [] ----
        if (Array.isArray(categoryNode)) {
          const categoryScores = categoryNode;
          if (!categoryScores.length) return;

          if (!brandScoresMap[llm]) brandScoresMap[llm] = {};
          brandScoresMap[llm][category] = categoryScores.map(item => item?.[type] ?? null);

          return;
        }

        // ---- CASE 2: Market only → category[market] = [] ----
        const marketNode = categoryNode[selectedMarket];
        if (!marketNode) return;

        // Persona NOT applied → normal market-level array
        if (Array.isArray(marketNode)) {
          const marketScores = marketNode;
          if (!marketScores.length) return;

          if (!brandScoresMap[llm]) brandScoresMap[llm] = {};
          brandScoresMap[llm][category] = marketScores.map(item => item?.[type] ?? null);

          return;
        }

        // ---- CASE 3: Market + Persona ----
        const personaNode = marketNode[selectedPersona];
        if (!personaNode || !Array.isArray(personaNode)) return;

        if (!brandScoresMap[llm]) brandScoresMap[llm] = {};
        brandScoresMap[llm][category] = personaNode.map(item => item?.[type] ?? null);
      });
    });

    return { brandScoresMap };
  };


  const getIndexScoreMetaData = () => {
    let indexScoreMetaData = [];
    llms.forEach((llm) => {
      const brandIndexScores = discoveryCtx?.brand_scores?.[llm] || {};
      const categories = Object.keys(brandIndexScores);
      categories.map((category) => {
        indexScoreMetaData[category] = {};
        const marketScores = brandIndexScores[category] || {};
        const personaScore = marketScores[selectedMarket] || {};
        const primaryPersonaScore = personaScore[selectedPersona] || [];
        indexScoreMetaData = indexScoreMetaData.concat(
          primaryPersonaScore.slice(0, 1).map((score) => {
            return {
              ...score,
              category: category,
              market: selectedMarket,
              llm: llm,
              persona: selectedPersona,
              geography: "",
            };
          })
        );
      });
    });
    return indexScoreMetaData;
  };


  return (
    <>
      <br />
      <div className={classes.dropdownWrapper}>
        <div className="Heading-checkbox">
          <h5>{`${discoveryType === "brand" ? ENTITY_LABELS.brand : ENTITY_LABELS.product} Summary`}</h5>

          {discoveryCtx.state.showReportCheckbox && (
            <Checkbox
              checked={checked}
              onChange={() => {
                discoveryCtx?.clickComponentCheckbox({
                  uniqueKey,
                  filter: {
                    id,
                    title: `${discoveryType === "brand" ? ENTITY_LABELS.brand : ENTITY_LABELS.product} Summary`,
                    tab: tab.brandOverview,
                    uniqueKey,
                    category: null,
                    llm: null,
                  },
                  checked,
                });
              }}
              inputProps={{ "aria-label": "controlled" }}
            />
          )}
        </div>

        <div className={classes.marketWrapper}>
          <MUISelect
            fullWidth={false}
            optionValue={discoveryCtx.state.selectedPersona}
            label={"Persona"}
            id={filterKey.persona}
            options={personaOptions}
            onChange={(e) => {
              console.log("changeValue",e.target.value)
              discoveryCtx.updateSelectedPersona(e.target.value);
            }}
          />
          <MUISelect
            fullWidth={false}
            optionValue={discoveryCtx.state.selectedMarket}
            label={"Market"}
            id={filterKey.market}
            options={(discoveryCtx?.markets || []).map((market) => ({
              name: market,
              value: market,
            }))}
            onChange={(e) => {
              discoveryCtx.updateSelectedMarket(e.target.value);
            }}
          />
          <div className={classes.shareIcon}>
            <MUIIconButton
              title="Click to Share"
              onClick={() => setShowShareModal(true)}
            >
              <ShareOutlinedIcon color="primary" />
            </MUIIconButton>
          </div>
        </div>
      </div>

      <hr />
      <MultiComparisonComponent
        fetchRealTimeActionsSources={() => { }}
        sourceURLIds={[]}
        reachSourceData={{}}
        sentimentSourceData={{}}
        categoryDimensionRatingSource={{}}
        secCategoryIndexScoreMetaData={{}}
        coreAttSourceData={{}}
        positiveAttSource={{}}
        negativeAttSource={{}}
        customerId={customerId}
        selectedInput={{
          llm: primary_llm,
          market: selectedMarket,
          persona: selectedPersona,
          brand: discoveryCtx.entity,
          product: discoveryCtx.type == "product" ? true : false,
          geography: "",
          category: "",
        }}
        productTaxonomyData={{}}
        secCategoryBrandFav={{}}
        secCategoryReach={{}}
        secCategoryBrandIndex={{}}
        indexScoreMetaData={getIndexScoreMetaData()}
        compCoreAttributes={{}}
        compCategoryDimensions={{}}
        pastCompCategoryDimensions={{}}
        pastCompCoreAttributes={{}}
        pastsecCategoryBrandIndex={{}}
        pastIndexScoreObj={{}}
        brandIndex={getBrandScores("total").brandScoresMap}
        brandReach={getBrandScores("rank").brandScoresMap}
        brandFav={getBrandScores("favorability").brandScoresMap}
        positiveAttributes={{}}
        negativeAttributes={{}}
        llmDomains={{}}
        geoLogo={{}}
        activeTab={"category"}
        brandDomains={{}}
        brandProduct={discoveryCtx.entity}
        type={discoveryCtx.type == "brand" ? "Brand" : "Product"}
        brandProductCategory={discoveryCtx.category}
        isBrandAuditPage={true}
        isBrandAuditMarketPage={true}
        isOverview={true}
        id={id}
        contextData={discoveryCtx}
      />
      {showShareModal &&
        <Modal
          height="60%"
          title="Share Link"
          open={showShareModal}
          handleClose={() => setShowShareModal(false)}
        >
          <Share setShowShareModal={setShowShareModal} />
        </Modal>
      }
    </>
  );
}
